import { useCallback, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { jsonParse, jsonStringify } from '@lyearn/core/utils/json';

type PersistantStateType<T> = {
  payload: T;
  version: number;
};

const usePersistentState = <T>(key: string, defaultValue: T, version: number) => {
  const [error, setError] = useState<any>(undefined);

  const serialize = useCallback(
    (value: PersistantStateType<T>) => {
      const { data, error } = jsonStringify(value);
      if (error) {
        setError(error);
      }
      return data ?? JSON.stringify(defaultValue);
    },
    [defaultValue],
  );

  const deserialize = useCallback(
    (value: string) => {
      const { data, error } = jsonParse<PersistantStateType<T>>(value);
      if (error) {
        setError(error);
      }
      return data ?? { payload: defaultValue, version };
    },
    [defaultValue, version],
  );

  const [_value, _setValue, removeValue] = useLocalStorage<PersistantStateType<T>>(
    key,
    {
      payload: defaultValue,
      version,
    },
    {
      raw: false,
      serializer: serialize,
      deserializer: deserialize,
    },
  );

  const value = _value && _value.version === version ? _value.payload : defaultValue;

  const setValue = useCallback(
    (value: T) => {
      _setValue({
        payload: value,
        version,
      });
    },
    [_setValue, version],
  );

  return { value, setValue, removeValue, error } as const;
};

export default usePersistentState;
